import React, { memo } from 'react';
import { formatCurrency } from '../../utilities/general-utils';

/**
 * Component to display comparison between current debts and new loan option
 */
const RefinanceComparison = memo(({ comparisonData, selectedAccounts, newLoanData }) => {
    if (!comparisonData) return null;

    const { current, newLoan } = comparisonData;
    
    // Calculate differences
    const balanceDiff = newLoan.totalBalance - current.totalBalance;
    const paymentDiff = newLoan.monthlyPayment - current.monthlyPayment;
    const interestDiff = newLoan.totalInterest - current.totalInterest;
    const timeDiff = newLoan.monthsToPayoff - current.monthsToPayoff;

    const getDiffColor = (diff, reverse = false) => {
        if (diff === 0) return '#666';
        const isGood = reverse ? diff > 0 : diff < 0;
        return isGood ? '#2e7d32' : '#d32f2f';
    };

    const getDiffIcon = (diff, reverse = false) => {
        if (diff === 0) return '—';
        const isGood = reverse ? diff > 0 : diff < 0;
        return isGood ? '↓' : '↑';
    };

    const formatDiff = (diff, isCurrency = true, isTime = false) => {
        if (diff === 0) return '—';
        const prefix = diff > 0 ? '+' : '';
        if (isTime) {
            return `${prefix}${diff} months (${(diff / 12).toFixed(1)} years)`;
        }
        return isCurrency ? `${prefix}${formatCurrency(Math.abs(diff))}` : `${prefix}${diff.toFixed(2)}%`;
    };

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '2px solid #ff9800',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem'
        }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#f57f17' }}>
                📊 Refinance Comparison Analysis
            </h3>

            {/* Summary Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {/* Current Scenario */}
                <div style={{
                    backgroundColor: '#ffebee',
                    border: '2px solid #f44336',
                    borderRadius: '8px',
                    padding: '1.5rem'
                }}>
                    <h4 style={{ margin: '0 0 1rem 0', color: '#c62828' }}>
                        Current Debt Scenario
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Total Balance:</span>
                            <strong>{formatCurrency(current.totalBalance)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Monthly Payment:</span>
                            <strong>{formatCurrency(current.monthlyPayment)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Total Interest:</span>
                            <strong>{formatCurrency(current.totalInterest)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Payoff Time:</span>
                            <strong>{current.monthsToPayoff} months ({(current.monthsToPayoff / 12).toFixed(1)} years)</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Accounts:</span>
                            <strong>{selectedAccounts.length}</strong>
                        </div>
                    </div>
                </div>

                {/* New Loan Scenario */}
                <div style={{
                    backgroundColor: '#e8f5e8',
                    border: '2px solid #4caf50',
                    borderRadius: '8px',
                    padding: '1.5rem'
                }}>
                    <h4 style={{ margin: '0 0 1rem 0', color: '#2e7d32' }}>
                        New {newLoanData.loanType}
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Loan Amount:</span>
                            <strong>{formatCurrency(newLoan.totalBalance)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Monthly Payment:</span>
                            <strong>{formatCurrency(newLoan.monthlyPayment)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Total Interest:</span>
                            <strong>{formatCurrency(newLoan.totalInterest)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Payoff Time:</span>
                            <strong>{newLoan.monthsToPayoff} months ({(newLoan.monthsToPayoff / 12).toFixed(1)} years)</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>APR:</span>
                            <strong>{parseFloat(newLoanData.apr || 0).toFixed(2)}%</strong>
                        </div>
                    </div>
                </div>
            </div>

            {/* Difference Analysis */}
            <div style={{
                backgroundColor: '#f3e5f5',
                border: '2px solid #9c27b0',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
            }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#7b1fa2' }}>
                    💰 Difference Analysis (New Loan vs Current)
                </h4>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                            Balance Difference
                        </div>
                        <div style={{ 
                            fontSize: '1.2rem', 
                            fontWeight: 'bold',
                            color: getDiffColor(balanceDiff)
                        }}>
                            {getDiffIcon(balanceDiff)} {formatDiff(balanceDiff)}
                        </div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                            Monthly Payment
                        </div>
                        <div style={{ 
                            fontSize: '1.2rem', 
                            fontWeight: 'bold',
                            color: getDiffColor(paymentDiff)
                        }}>
                            {getDiffIcon(paymentDiff)} {formatDiff(paymentDiff)}
                        </div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                            Total Interest
                        </div>
                        <div style={{ 
                            fontSize: '1.2rem', 
                            fontWeight: 'bold',
                            color: getDiffColor(interestDiff)
                        }}>
                            {getDiffIcon(interestDiff)} {formatDiff(interestDiff)}
                        </div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                            Payoff Time
                        </div>
                        <div style={{ 
                            fontSize: '1.2rem', 
                            fontWeight: 'bold',
                            color: getDiffColor(timeDiff)
                        }}>
                            {getDiffIcon(timeDiff)} {formatDiff(timeDiff, false, true)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Recommendation */}
            <div style={{
                padding: '1.5rem',
                backgroundColor: interestDiff < 0 ? '#e8f5e8' : '#fff3e0',
                border: `2px solid ${interestDiff < 0 ? '#4caf50' : '#ff9800'}`,
                borderRadius: '8px'
            }}>
                <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    color: interestDiff < 0 ? '#2e7d32' : '#f57f17' 
                }}>
                    {interestDiff < 0 ? '✅ Potential Savings' : '⚠️ Analysis Summary'}
                </h4>
                
                {interestDiff < 0 ? (
                    <div>
                        <p style={{ margin: '0 0 1rem 0', fontSize: '1.05rem' }}>
                            <strong>Great news!</strong> This refinance could save you <strong>{formatCurrency(Math.abs(interestDiff))}</strong> in total interest over the life of your debts.
                        </p>
                        
                        {paymentDiff < 0 && (
                            <p style={{ margin: '0 0 1rem 0' }}>
                                You'll also reduce your monthly payments by <strong>{formatCurrency(Math.abs(paymentDiff))}</strong>, 
                                improving your monthly cash flow.
                            </p>
                        )}
                        
                        {timeDiff < 0 && (
                            <p style={{ margin: '0' }}>
                                Additionally, you'll pay off your debt <strong>{Math.abs(timeDiff)} months sooner</strong>, 
                                giving you financial freedom faster.
                            </p>
                        )}
                    </div>
                ) : (
                    <div>
                        <p style={{ margin: '0 0 1rem 0', fontSize: '1.05rem' }}>
                            This refinance would cost an additional <strong>{formatCurrency(interestDiff)}</strong> in total interest.
                        </p>
                        
                        {paymentDiff < 0 ? (
                            <p style={{ margin: '0' }}>
                                However, you would reduce your monthly payments by <strong>{formatCurrency(Math.abs(paymentDiff))}</strong>, 
                                which might help with monthly cash flow despite the higher total cost.
                            </p>
                        ) : (
                            <p style={{ margin: '0' }}>
                                Consider shopping for better rates or terms to make this refinance more beneficial.
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Account Breakdown */}
            <div style={{ marginTop: '2rem' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>
                    Accounts Included in Analysis
                </h4>
                <div style={{
                    display: 'grid',
                    gap: '0.5rem'
                }}>
                    {selectedAccounts.map((account, index) => (
                        <div 
                            key={account.id}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '0.75rem',
                                backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                borderRadius: '4px',
                                fontSize: '0.9rem'
                            }}
                        >
                            <span>{account.name}</span>
                            <span>{formatCurrency(account.balance)} @ {(parseFloat(account.apr || 0) * 100).toFixed(2)}%</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
});

RefinanceComparison.displayName = 'RefinanceComparison';

export default RefinanceComparison;